import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, Building2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { teamsApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import AuthModal from '../components/AuthModal';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

/**
 * /teams/accept/:token — public preview + authenticated accept.
 *
 * Flow:
 *  1. On mount, fetch invite preview (public) → show org name.
 *  2. If user not authenticated, show CTA to sign in (modal).
 *  3. If authenticated, big green "Join {org}" button → POST accept → done.
 */
const AcceptInvite = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAuth, setShowAuth] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(null);

  const loadPreview = useCallback(async () => {
    try {
      const r = await teamsApi.invitePreview(token);
      setPreview(r);
    } catch (err) {
      setError(err.message || 'Invite not found');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  const handleAccept = async () => {
    if (!isAuthenticated) {
      setShowAuth(true);
      return;
    }
    setAccepting(true);
    try {
      const r = await teamsApi.acceptInvite(token);
      setAccepted(r);
      setTimeout(() => navigate('/teams'), 1500);
    } catch (err) {
      setError(err.message || 'Could not accept invite');
    } finally {
      setAccepting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar />
      <div className="max-w-2xl mx-auto px-6 py-20">
        <Card className="bg-[#0F0F0F] border-gray-800" data-testid="accept-invite-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl text-white">
              <Building2 className="w-6 h-6 text-[#00FF00]" />
              Team invitation
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading && (
              <div className="flex items-center gap-2 text-gray-400">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading invite…
              </div>
            )}
            {!loading && error && (
              <div className="flex items-start gap-2 text-[#FF0055]" data-testid="accept-invite-error">
                <AlertTriangle className="w-5 h-5 mt-0.5" />
                <div>
                  <p className="font-semibold">{error}</p>
                  <p className="text-sm text-gray-500 mt-1">
                    The link may have expired or already been used.
                  </p>
                </div>
              </div>
            )}
            {!loading && !error && preview && !accepted && (
              <>
                <p className="text-gray-300 mb-6">
                  You've been invited to join{' '}
                  <span
                    className="text-[#00FF00] font-semibold"
                    data-testid="accept-invite-org-name"
                  >
                    {preview.org_name}
                  </span>{' '}
                  on CyberInsti. Joining unlocks Pro features for the team's billing period.
                </p>
                <Button
                  onClick={handleAccept}
                  disabled={accepting}
                  data-testid="accept-invite-btn"
                  className="bg-[#00FF00] text-black hover:bg-[#00DD00] font-semibold"
                >
                  {accepting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : isAuthenticated ? (
                    'Join the team'
                  ) : (
                    'Sign in to join'
                  )}
                </Button>
              </>
            )}
            {accepted && (
              <div
                className="flex items-center gap-2 text-[#00FF00]"
                data-testid="accept-invite-success"
              >
                <CheckCircle2 className="w-5 h-5" />
                Joined! Redirecting to your team dashboard…
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      {showAuth && (
        <AuthModal
          isOpen={showAuth}
          onClose={() => setShowAuth(false)}
          initialTab="login"
        />
      )}
      <Footer />
    </div>
  );
};

export default AcceptInvite;
